import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useCallback,
} from "react";
import {
  getProperties,
  getUserPropertyPosts,
  publishProperty as publishPropertyService,
  deletePropertyById,
  getPropertyCountByCategory,
  updatePropertyData,
  searchPropertiesListing,
} from "../services/propertyService";
import { PropertyListing } from "../types/Property";
import { useCompany } from "./CompanyContext";
import { useAuth } from "./AuthContext";

interface PropertyContextType {
  properties: PropertyListing[];
  filteredProperties: PropertyListing[];
  userPropertyPosts: PropertyListing[];
  selectedProperty: PropertyListing | null;
  category: string | null;
  loading: boolean;
  error: string | null;
  propertyCountByCategory: Record<string, number>;
  fetchProperties: (page?: number, pageSize?: number, reset?: boolean) => void;
  fetchUserPropertyPosts: (userId: number, companyId?: number) => Promise<void>;
  getUserPropertyPosts: (userId: number, companyId?: number) => void;
  publishProperty: (formData: FormData) => Promise<void>;
  updateProperty: (field: string, formData: FormData) => Promise<void>;
  deleteProperty: (formData: FormData) => Promise<boolean>;
  handleSetSelectedProperty: (property: PropertyListing | null) => void;
  filterPropertiesByCategory: (category: string) => void;
  filterPropertiesByMode: (mode: string) => void;
  filterPropertiesByProvinceAndCanton: (
    selectedProvinces: string[],
    selectedCantons: string[]
  ) => void;
  filterPropertiesByLocation: (
    selectedProvinces: string[],
    selectedCantons: string[],
    selectedDistricts: string[]
  ) => void;
  filterProperties: (filters: {
    transactionType?: string;
    priceRange?: [number, number];
    selectedCategories?: string[];
    selectedServices?: string[];
    selectedAmenities?: string[];
    currency?: string;
    selectedProvince?: string;
    selectedCanton?: string;
    selectedDistrito?: string;
    query?: string;
    selectedPublicationDate?: string;
  }) => void;
  searchProperties: (searchTerm: string) => void;
  toggleFavorite: (propertyId: number) => void;
  isFavorite: (propertyId: number) => boolean;
  getFavoriteProperties: () => PropertyListing[];
  favorites: Set<number>;
  totalProperties: number;
  isOpenChatBubble?: boolean;
  setIsOpenChatBubble?: (isOpen: boolean) => void;
  searchTermForAri?: string;
  setSearchTermForAri?: (term: string) => void;
  isTextFromSearch?: boolean;
  setIsTextFromSearch?: (isText: boolean) => void;
  searchPropertiesListings: (searchTerm: string) => void;
}

const PropertyContext = createContext<PropertyContextType | undefined>(
  undefined
);

export const PropertyProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [properties, setProperties] = useState<PropertyListing[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<
    PropertyListing[]
  >([]);
  const [userPropertyPosts, setUserPropertyPosts] = useState<PropertyListing[]>(
    []
  );
  const [category, setCategory] = useState<string>("Todas");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] =
    useState<PropertyListing | null>(
      localStorage.getItem("selectedProperty")
        ? JSON.parse(localStorage.getItem("selectedProperty") as string)
        : null
    );
  const [favorites, setFavorites] = useState<Set<number>>(() => {
    const storedFavorites = localStorage.getItem("favorites");
    return storedFavorites ? new Set(JSON.parse(storedFavorites)) : new Set();
  });

  const [propertyCountByCategory, setPropertyCountByCategory] = useState<
    Record<string, number>
  >({});
  const [totalProperties, setTotalProperties] = useState<number>(0);
  const [isOpenChatBubble, setIsOpenChatBubble] = useState<boolean>(false);
  const [searchTermForAri, setSearchTermForAri] = useState<string>("");
  const [isTextFromSearch, setIsTextFromSearch] = useState<boolean>(false);

  const { company } = useCompany();
  const {userToken} = useAuth();

 const fetchProperties = useCallback(
  async (
    page = 1,
    pageSize = 200,
    reset = false,
    companyOnly = false,
    companyIdParam?: number
  ): Promise<PropertyListing[]> => {
    const companyId = companyOnly ? companyIdParam ?? company?.id : undefined;
    if (companyOnly && !companyId) return [];

    setLoading(true);
    setError(null);

    try {

      console.log(companyId)
      
      const data = await getProperties(page, pageSize, companyId);

      const filteredData = data.items.filter(
        (property: PropertyListing) => property.moduleStateId === 1
      );

      if (reset) {
        setProperties(filteredData);
        setFilteredProperties(filteredData);
      } else {
        setProperties((prev) => [...prev, ...filteredData]);
        setFilteredProperties((prev) => [...prev, ...filteredData]);
      }

      setTotalProperties(data.totalCount);
      return filteredData;
    } catch (err) {
      setError("Failed to fetch properties");
      return [];
    } finally {
      setLoading(false);
    }
  },
  [company]
);


const searchPropertiesListings = async (searchTerm: string) => {
  try {
    const lowerCasedTerm = searchTerm.toLowerCase().trim();
    const data = await searchPropertiesListing(lowerCasedTerm) as unknown as PropertyListing[];
    setFilteredProperties(data);
  } catch (err) {
    setError("Failed to search properties");
  }
};

  const fetchPropertyCountByCategory = useCallback(
    async (companyIdParam?: number) => {
      const companyId = companyIdParam ?? company?.id;

      // Si queremos contar solo propiedades de compañía y no hay companyId, salimos
      if (!companyId && window.location.pathname.startsWith("/company/")) return;

      setLoading(true);
      setError(null);

      try {
        const data = await getPropertyCountByCategory(companyId);
        setPropertyCountByCategory(data);

        if (data?.Todas !== undefined) {
          setTotalProperties(data.Todas);
        }
      } catch (err) {
        console.error("Failed to fetch property count by category:", err);
        setError("Failed to fetch property count by category");
      } finally {
        setLoading(false);
      }
    },
    [company] // Dependemos solo de company para fallback
  );




  const fetchUserPropertyPosts = async (userId: number, companyId?: number) => {
    setLoading(true);
    setError(null);

    try {
      const userPosts = await getUserPropertyPosts(userId, companyId);

      if (Array.isArray(userPosts)) {
        setUserPropertyPosts(userPosts);
      } else {
        setUserPropertyPosts([]);
      }
    } catch (err) {
      console.error("Error fetching user property posts:", err);
      setError("Failed to fetch user property posts");
      setUserPropertyPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyProperties = useCallback(async () => {
  const isCompanyRoute = window.location.pathname.startsWith("/company/");

  // Si es ruta de compañía pero aún no hay compañía cargada, no hacer nada
  if (isCompanyRoute && !company) return;

  try {
    if (company) {
      // ✅ Solo propiedades de la compañía
      await fetchProperties(1, 200, true, true, company.id);
      await fetchPropertyCountByCategory(company.id);
    } else if (!isCompanyRoute) {
      // ✅ Solo muro público si NO estamos en URL de compañía
      await fetchProperties(1, 200, true, false);
      await fetchPropertyCountByCategory();
    }
  } catch (err) {
    console.error("Error cargando propiedades:", err);
  }
}, [company, fetchProperties, fetchPropertyCountByCategory]);


  useEffect(() => {
  const isCompanyRoute = window.location.pathname.startsWith("/company/");

  // 🕒 Evita ejecutar el fetch si estamos en modo compañía pero aún no está lista
  if (isCompanyRoute && !company) return;

  fetchCompanyProperties();
}, [company, fetchCompanyProperties]);




  const publishProperty = async (formData: FormData) => {
    setLoading(true);
    try {
      const response = await publishPropertyService(formData);
      if (response?.status === 200) {
        fetchProperties();
        fetchPropertyCountByCategory();
        fetchUserPropertyPosts(userToken!.user.id, company?.id);
        localStorage.removeItem("uploadedPhotos");
        localStorage.removeItem("propertyData");
      } else {
        alert("Failed to publish property");
      }
    } catch (err) {
      setError("Failed to publish property");
    } finally {
      setLoading(false);
    }
  };

  const updateProperty = async (field: string, formData: FormData) => {
    setLoading(true);
    try {
      const response = await updatePropertyData(field, formData);
      if (response?.status === 200) {
        fetchProperties();
        fetchUserPropertyPosts(userToken!.user.id, company?.id);
        handleSetSelectedProperty(response.data);
        localStorage.removeItem("uploadedPhotos");
        localStorage.removeItem("propertyData");
      } else {
        alert(`Failed to update property ${field}`);
      }
    } catch (err) {
      setError(`Failed to update property ${field}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteProperty = async (formData: FormData): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await deletePropertyById(formData);
      if (response?.status === 200) {
        fetchProperties();
        fetchUserPropertyPosts(userToken!.user.id, company?.id);
        fetchPropertyCountByCategory();
        localStorage.removeItem("uploadedPhotos");
        localStorage.removeItem("propertyData");
        return true;
      } else {
        setError("Failed to delete property");
        return false;
      }
    } catch (err) {
      setError("Failed to delete property");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSetSelectedProperty = (property: PropertyListing | null) => {
    setSelectedProperty(property);
    localStorage.setItem("selectedProperty", JSON.stringify(property));
  };

  const filterPropertiesByLocation = (
    selectedProvinces: string[],
    selectedCantons: string[],
    selectedDistricts: string[]
  ) => {
    let filteredProperties = properties.filter(
      (property) => property.moduleStateId === 1
    );

    if (
      selectedProvinces.length === 0 &&
      selectedCantons.length === 0 &&
      selectedDistricts.length === 0
    ) {
      setFilteredProperties(filteredProperties);
      return;
    }

    if (selectedDistricts.length > 0) {
      filteredProperties = filteredProperties.filter((property) => {
        return selectedDistricts.some((districtKey) => {
          const [provinceName, cantonName, districtName] =
            districtKey.split("-");

          const addressHasProvince = property.address_Description
            .toLowerCase()
            .includes(provinceName.toLowerCase());

          const cantonMatch = property.county
            ?.toLowerCase()
            .includes(cantonName.toLowerCase());
          const districtMatch = property.district
            ?.toLowerCase()
            .includes(districtName.toLowerCase());

          return addressHasProvince && cantonMatch && districtMatch;
        });
      });
    } else if (selectedCantons.length > 0) {
      filteredProperties = filteredProperties.filter((property) => {
        return selectedCantons.some((cantonKey) => {
          const [provinceName, cantonName] = cantonKey.split("-");

          const addressHasProvince = property.address_Description
            .toLowerCase()
            .includes(provinceName.toLowerCase());

          const cantonMatch = property.county
            ?.toLowerCase()
            .includes(cantonName.toLowerCase());

          return addressHasProvince && cantonMatch;
        });
      });
    } else if (selectedProvinces.length > 0) {
      filteredProperties = filteredProperties.filter((property) => {
        return selectedProvinces.some((province) =>
          property.address_Description
            .toLowerCase()
            .includes(province.toLowerCase())
        );
      });
    }

    setFilteredProperties(filteredProperties);
  };

  const filterPropertiesByCategory = (category: string) => {
    let filtered = properties.filter(
      (property) => property.moduleStateId === 1
    );
    if (category === "Novedad") {
      const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      filtered = filtered.filter(
        (property) => new Date(property.createdDate).getTime() >= oneWeekAgo
      );
    } else if (category !== "Todas" && category !== "No disponible") {
      filtered = filtered.filter(
        (property) => property.categoryName === category
      );
    } else if (category === "No disponible") {
      filtered = properties.filter((property) => property.moduleStateId !== 1);
    }
    setFilteredProperties(filtered);
    setCategory(category);

  };

  const filterPropertiesByMode = (mode: string) => {
    let filteredByMode = properties;
    if (mode !== "Todos") {
      filteredByMode = filteredByMode.filter(
        (property) =>
          property.modeDescription === mode && property.moduleStateId === 1
      );
    }
    setFilteredProperties(filteredByMode);
  };

  const filterPropertiesByProvinceAndCanton = (
    selectedProvinces: string[],
    selectedCantons: string[]
  ) => {
    let filteredByProvinceAndCanton = properties;

    if (selectedProvinces.length === 0 && selectedCantons.length === 0) {
      filteredByProvinceAndCanton = properties;
    } else {
      filteredByProvinceAndCanton = filteredByProvinceAndCanton.filter(
        (property) => {
          const normalizedDescription =
            property.address_Description.toLowerCase();

          const isProvinceMatch = selectedProvinces.some((province) =>
            normalizedDescription.includes(province.toLowerCase())
          );

          return isProvinceMatch;
        }
      );

      if (selectedCantons.length > 0) {
        filteredByProvinceAndCanton = filteredByProvinceAndCanton.filter(
          (property) => {
            const normalizedCanton = property.county
              ? property.county.toLowerCase()
              : "";

            const isCantonMatch = selectedCantons.some((canton) => {
              const cantonWithoutProvince = canton.split("-")[1]?.trim();
              return (
                cantonWithoutProvince &&
                normalizedCanton.includes(cantonWithoutProvince.toLowerCase())
              );
            });

            return isCantonMatch;
          }
        );
      }
    }

    setFilteredProperties(filteredByProvinceAndCanton);
  };

  const filterProperties = (filters: {
    transactionType?: string;
    priceRange?: [number, number];
    selectedCategories?: string[];
    selectedServices?: string[];
    selectedAmenities?: string[];
    currency?: string;
    selectedProvince?: string;
    selectedCanton?: string;
    selectedDistrito?: string;
    query?: string;
    selectedPublicationDate?: string;
  }) => {
    const { transactionType, priceRange } = filters;
    let filtered = properties.filter(
      (property) => property.moduleStateId === 1
    );

    if (transactionType && transactionType !== "Todos") {
      filtered = filtered.filter(
        (property) => property.modeDescription === transactionType
      );
    }

    if (priceRange && priceRange.length === 2) {
      const [minPrice, maxPrice] = priceRange;
      filtered = filtered.filter(
        (property) => property.price >= minPrice && property.price <= maxPrice
      );
    }

    if (filters.selectedCategories && filters.selectedCategories.length > 0) {
      filtered = filtered.filter((property) => {
        return filters.selectedCategories!.some((category) =>
          property.categoryName?.toLowerCase().includes(category.toLowerCase())
        );
      });
    }

    if (filters.selectedServices && filters.selectedServices.length > 0) {
      filtered = filtered.filter((property) => {
        return filters.selectedServices!.every((service) =>
          property.services?.includes(service)
        );
      });
    }

    if (filters.selectedAmenities && filters.selectedAmenities.length > 0) {
      filtered = filtered.filter((property) => {
        return filters.selectedAmenities!.every((amenity) =>
          property.amenities?.includes(amenity)
        );
      });
    }

    if (filters.currency && filters.currency !== "all") {
      filtered = filtered.filter(
        (property) => property.currency === filters.currency
      );
    }

    // Filtro por fecha de publicación
    if (filters.selectedPublicationDate && filters.selectedPublicationDate !== "all") {
      const now = new Date();
      let dateThreshold: Date;

      switch (filters.selectedPublicationDate) {
        case "last24hours":
          dateThreshold = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case "last3days":
          dateThreshold = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
          break;
        case "last7days":
          dateThreshold = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case "last30days":
          dateThreshold = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          dateThreshold = new Date(0);
      }

      filtered = filtered.filter((property) => {
        if (!property.createdDate) return false;
        
        const propertyDate = property.createdDate instanceof Date 
          ? property.createdDate 
          : new Date(property.createdDate);
        
        return propertyDate >= dateThreshold;
      });
    }

    if (filters.query) {
      const lowerCasedQuery = filters.query.toLowerCase().trim();
      filtered = filtered.filter((property) => {
        return (
          property.title?.toLowerCase().includes(lowerCasedQuery) ||
          property.description?.toLowerCase().includes(lowerCasedQuery) ||
          property.categoryName?.toLowerCase().includes(lowerCasedQuery) ||
          property.amenities?.toLowerCase().includes(lowerCasedQuery) ||
          property.services?.toLowerCase().includes(lowerCasedQuery) ||
          property.modeDescription?.toLowerCase().includes(lowerCasedQuery) ||
          property.address_Description
            ?.toLowerCase()
            .includes(lowerCasedQuery) ||
          property.county?.toLowerCase().includes(lowerCasedQuery) ||
          property.district?.toLowerCase().includes(lowerCasedQuery) ||
          property.zone?.toLowerCase().includes(lowerCasedQuery)
        );
      });
    }

    if (filters.selectedProvince) {
      const matchExists = filtered.some((property) => {
        const normalizedDescription =
          property.address_Description?.toLowerCase() || "";
        return normalizedDescription.includes(
          filters.selectedProvince!.toLowerCase()
        );
      });

      if (matchExists) {
        filtered = filtered.filter((property) => {
          const normalizedDescription =
            property.address_Description?.toLowerCase() || "";
          return normalizedDescription.includes(
            filters.selectedProvince!.toLowerCase()
          );
        });
      }
    }

    if (filters.selectedCanton) {
      const matchExists = filtered.some((property) => {
        const normalizedCanton = property.county?.toLowerCase() || "";
        const normalizedDescription =
          property.address_Description?.toLowerCase() || "";
        return (
          normalizedCanton.includes(filters.selectedCanton!.toLowerCase()) ||
          normalizedDescription.includes(filters.selectedCanton!.toLowerCase())
        );
      });

      if (matchExists) {
        filtered = filtered.filter((property) => {
          const normalizedCanton = property.county?.toLowerCase() || "";
          const normalizedDescription =
            property.address_Description?.toLowerCase() || "";
          return (
            normalizedCanton.includes(filters.selectedCanton!.toLowerCase()) ||
            normalizedDescription.includes(
              filters.selectedCanton!.toLowerCase()
            )
          );
        });
      }
    }

    if (filters.selectedDistrito) {
      const matchExists = filtered.some((property) => {
        const normalizedDescription =
          property.address_Description?.toLowerCase() || "";
        return normalizedDescription.includes(
          filters.selectedDistrito!.toLowerCase()
        );
      });

      if (matchExists) {
        filtered = filtered.filter((property) => {
          const normalizedDescription =
            property.address_Description?.toLowerCase() || "";
          return normalizedDescription.includes(
            filters.selectedDistrito!.toLowerCase()
          );
        });
      }
    }

    setFilteredProperties(filtered);
  };

  const searchProperties = (searchTerm: string) => {
    const lowerCasedTerm = searchTerm.toLowerCase().trim();
    const numericSearch = parseFloat(searchTerm.replace(/[^\d.]/g, ""));

    const filtered = properties.filter((property) => {
      // Si contiene #, buscar solo en ID
      if (searchTerm.includes("#")) {
        // Limpiar completamente: quitar #, espacios múltiples, y trim
        const cleanTerm = searchTerm
          .replace("#", "")
          .replace(/\s+/g, "")
          .toLowerCase();
        const propertyId = property.id_Property
          ?.toString()
          .replace(/\s+/g, "")
          .toLowerCase();
        return propertyId?.includes(cleanTerm);
      }

      // Si NO contiene #, buscar en precio y otros campos (excepto ID)
      const priceMatch = !isNaN(numericSearch)
        ? property.price === numericSearch ||
          property.price.toString().includes(numericSearch.toString())
        : false;

      return (
        property.title?.toLowerCase().includes(lowerCasedTerm) ||
        property.description?.toLowerCase().includes(lowerCasedTerm) ||
        property.categoryName?.toLowerCase().includes(lowerCasedTerm) ||
        property.address_Description?.toLowerCase().includes(lowerCasedTerm) ||
        property.county?.toLowerCase().includes(lowerCasedTerm) ||
        property.district?.toLowerCase().includes(lowerCasedTerm) ||
        property.zone?.toLowerCase().includes(lowerCasedTerm) ||
        property.promotion?.title?.toLowerCase().includes(lowerCasedTerm) ||
        property.coupon?.title?.toLowerCase().includes(lowerCasedTerm) ||
        priceMatch
      );
    });

    setFilteredProperties(filtered);
  };

  const toggleFavorite = (propertyId: number) => {
    const updatedFavorites = new Set(favorites);
    if (updatedFavorites.has(propertyId)) {
      updatedFavorites.delete(propertyId);
    } else {
      updatedFavorites.add(propertyId);
    }
    setFavorites(updatedFavorites);
    localStorage.setItem(
      "favorites",
      JSON.stringify(Array.from(updatedFavorites))
    );
  };

  const isFavorite = (propertyId: number) => {
    return favorites.has(propertyId);
  };

  const getFavoriteProperties = () => {
    return properties.filter((property) => favorites.has(property.id_Property));
  };

  return (
    <PropertyContext.Provider
      value={{
        properties,
        filteredProperties,
        selectedProperty,
        category,
        loading,
        error,
        propertyCountByCategory,
        fetchProperties,
        fetchUserPropertyPosts,
        getUserPropertyPosts,
        publishProperty,
        updateProperty,
        deleteProperty,
        handleSetSelectedProperty,
        filterPropertiesByCategory,
        filterProperties,
        filterPropertiesByMode,
        filterPropertiesByProvinceAndCanton,
        filterPropertiesByLocation,
        userPropertyPosts,
        searchProperties,
        toggleFavorite,
        isFavorite,
        getFavoriteProperties,
        favorites,
        totalProperties,
        isOpenChatBubble,
        setIsOpenChatBubble,
        searchTermForAri,
        setSearchTermForAri,
        isTextFromSearch,
        setIsTextFromSearch,
        searchPropertiesListings
      }}
    >
      {children}
    </PropertyContext.Provider>
  );
};

export const useProperty = () => {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error("useProperty must be used within a PropertyProvider");
  }
  return context;
};
